// Site-wide navigation and integration data. Pages render from this, so adding
// a link, a solution page or an integration is a one-line change here.
import gmail from "@/assets/gmail.svg";
import outlook from "@/assets/outlook.svg";
import slack from "@/assets/slack.svg";
import teams from "@/assets/teams.svg";
import googleCalendar from "@/assets/google-calendar.png";
import notion from "@/assets/notion.svg";
import hubspot from "@/assets/hubspot.svg";
import { products, productPath } from "./products";

export interface NavLinkItem {
  label: string;
  href: string;
  description?: string;
  /** A product's mark, shown beside the label in menus. */
  icon?: string;
  /** true = leaves the React app (login, dashboard, static pages). */
  external?: boolean;
}

export interface NavGroup {
  label: string;
  /** Present when the group is a plain link instead of a menu. */
  href?: string;
  columns?: { heading: string; links: NavLinkItem[] }[];
}

export interface Integration {
  key: string;
  name: string;
  logo: string;
  href?: string;
}

// Only tools a ClearMakes product really connects to.
export const integrations: Integration[] = [
  { key: "gmail", name: "Gmail", logo: gmail, href: "/gmail-integration" },
  { key: "outlook", name: "Outlook", logo: outlook, href: "/outlook-integration" },
  { key: "slack", name: "Slack", logo: slack, href: "/slack-integration" },
  { key: "teams", name: "Microsoft Teams", logo: teams, href: "/teams-integration" },
  { key: "google-calendar", name: "Google Calendar", logo: googleCalendar },
  { key: "notion", name: "Notion", logo: notion },
  { key: "hubspot", name: "HubSpot", logo: hubspot, href: "/integrations/email-to-hubspot" },
];

export const integrationByKey = (key: string) => integrations.find((i) => i.key === key);

/** The blue strip on top of the menu on every page. One line; change it here. */
export const ANNOUNCEMENT = {
  tag: "New",
  title: "ClearMeet for Windows.",
  line: "Live answers while the meeting is still happening.",
  cta: "Download",
  href: "https://meet.clearmakes.com/download",
};

export const REQUEST_DEMO_URL = "/request-demo";
/** ClearMakes Agents runs on its own subdomain. */
export const AGENTS_URL = "https://agents.clearmakes.com";
export const GET_STARTED_URL = "/get-started";
export const LOGIN_URL = "/login";

export const mainNav: NavGroup[] = [
  {
    label: "Products",
    columns: [
      {
        heading: "Products",
        links: [
          ...products.map((p) => ({ label: p.name, href: productPath(p), description: p.tagline, icon: p.logo })),
          { label: "All products", href: "/products", description: "Everything ClearMakes builds, in one place." },
        ],
      },
      {
        heading: "Inside Inbox",
        links: [
          { label: "Integrations", href: "/integrations" },
          { label: "Workflows", href: "/workflows" },
          { label: "Smart FAQ", href: "/smart-faq" },
          { label: "Security", href: "/security-architecture" },
        ],
      },
      {
        heading: "Inside Agents",
        links: [
          { label: "Website chatbot", href: `${AGENTS_URL}/ai-chatbot-for-website` },
          { label: "Internal assistant", href: `${AGENTS_URL}/internal-ai-assistant` },
          { label: "MCP and apps", href: `${AGENTS_URL}/mcp-ai-agent` },
          { label: "Use cases", href: `${AGENTS_URL}/#team` },
        ],
      },
    ],
  },
  {
    label: "Solutions",
    columns: [
      {
        heading: "By team",
        links: [
          { label: "Founders", href: "/for-founders", description: "Hand the routine emails to workflows." },
          { label: "Remote teams", href: "/for-remote-teams", description: "Each important email in the right channel." },
          { label: "Freelancers", href: "/for-freelancers", description: "Answer common client questions for you." },
        ],
      },
      {
        heading: "By use case",
        links: [
          { label: "Workflows", href: "/workflows" },
          { label: "Smart FAQ", href: "/smart-faq" },
          { label: "All solutions", href: "/solutions" },
        ],
      },
    ],
  },
  {
    label: "Resources",
    columns: [
      {
        heading: "Learn",
        links: [
          { label: "Documentation", href: "/documentation", description: "Set up and get the most from your workspace." },
          { label: "Resources", href: "/resources", description: "Guides, comparisons and how-tos." },
          { label: "Guides", href: "/guides" },
        ],
      },
      {
        heading: "Trust",
        links: [
          { label: "Security architecture", href: "/security-architecture" },
          { label: "Privacy", href: "/privacy" },
          { label: "Report a vulnerability", href: "/vulnerability" },
        ],
      },
    ],
  },
  {
    label: "Pricing",
    columns: [
      {
        heading: "Pricing",
        links: [
          { label: "ClearMakes Inbox", href: "/pricing", description: "Plans for one inbox or a whole team." },
          { label: "ClearMakes Agents", href: `${AGENTS_URL}/pricing`, description: "Free to start, paid plans from $12 a month." },
          { label: "ClearMeet", href: "https://meet.clearmakes.com/pricing", description: "5 free meetings a month, on Windows." },
        ],
      },
    ],
  },
  {
    label: "Company",
    columns: [
      {
        heading: "Company",
        links: [
          { label: "About", href: "/company", description: "Who we are and what we are building." },
          { label: "Careers", href: "/careers" },
          { label: "Contact", href: "/contact" },
        ],
      },
    ],
  },
];

export const footerColumns: { heading: string; links: NavLinkItem[] }[] = [
  {
    heading: "Products",
    links: [
      ...products.map((p) => ({ label: p.name, href: productPath(p) })),
      { label: "All products", href: "/products" },
      { label: "Inbox pricing", href: "/pricing" },
      { label: "Agents pricing", href: `${AGENTS_URL}/pricing` },
      { label: "ClearMeet pricing", href: "https://meet.clearmakes.com/pricing" },
      { label: "Integrations", href: "/integrations" },
    ],
  },
  {
    heading: "Solutions",
    links: [
      { label: "Founders", href: "/for-founders" },
      { label: "Remote teams", href: "/for-remote-teams" },
      { label: "Freelancers", href: "/for-freelancers" },
      { label: "All solutions", href: "/solutions" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Documentation", href: "/documentation" },
      { label: "Resources", href: "/resources" },
      { label: "Security", href: "/security-architecture" },
      { label: "Sub-processors", href: "/guidelines" },
      { label: "Acceptable use", href: "/acceptable-use" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/company" },
      { label: "Careers", href: "/careers" },
      { label: "Press", href: "/press" },
      { label: "Contact", href: "/contact" },
      { label: "Request a demo", href: REQUEST_DEMO_URL },
    ],
  },
];

// The footer's second row (the first is footerColumns; Legal sits at its right end).
export const footerExtraColumns: { heading: string; links: NavLinkItem[] }[] = [
  {
    heading: "Get started",
    links: [
      { label: "Start free", href: GET_STARTED_URL },
      { label: "Log in", href: LOGIN_URL },
      { label: "Download ClearMeet for Windows", href: "https://meet.clearmakes.com/download" },
      { label: "Request a demo", href: REQUEST_DEMO_URL },
    ],
  },
  {
    heading: "Guides",
    links: [
      { label: "Zapier alternatives", href: "/alternatives/zapier" },
      { label: "Gmail to Slack", href: "/integrations/gmail-to-slack" },
      { label: "Outlook to Teams", href: "/integrations/outlook-to-teams" },
      { label: "All guides", href: "/guides" },
    ],
  },
];

export const legalLinks: NavLinkItem[] = [
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
  { label: "Refunds", href: "/refund" },
  { label: "Acceptable use", href: "/acceptable-use" },
  { label: "Cookies", href: "/cookies" },
  { label: "Report a vulnerability", href: "/vulnerability" },
];

// The site is written in English. Other languages open a machine translation,
// and the menu says so rather than implying a native translation exists.
export const languages: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "hi", label: "हिन्दी" },
  { code: "ja", label: "日本語" },
];
